/**
 * Environment-specific live tracking domain resolver and link generators.
 * Supported environments:
 * - dev: https://live-dev.trucktrack.zone
 * - stage: https://live-stage.trucktrack.zone
 * - preprod: https://live-preprod.trucktrack.zone
 * - prod: https://live.trucktrack.zone
 */

export const LIVE_DOMAINS = {
  dev: "https://live-dev.trucktrack.zone",
  stage: "https://live-stage.trucktrack.zone",
  preprod: "https://live-preprod.trucktrack.zone",
  prod: "https://live.trucktrack.zone"
};

/**
 * Returns the base URL for the live tracking app based on the current environment.
 * Evaluates in order:
 * 1. Build-time injected TT_LIVE_URL (if configured)
 * 2. Runtime browser hostname auto-detection
 * 3. Default fallback to dev
 */
export function getLiveBaseUrl() {
  // 1. Build-time injected override
  try {
    if (typeof TT_LIVE_URL !== "undefined" && TT_LIVE_URL) {
      return String(TT_LIVE_URL).replace(/\/+$/, "");
    }
  } catch (_) {}

  // 2. Runtime browser hostname auto-detection
  if (typeof window !== "undefined" && window.location && window.location.hostname) {
    const host = window.location.hostname.toLowerCase();
    if (host.includes("preprod")) return LIVE_DOMAINS.preprod;
    if (host.includes("stage")) return LIVE_DOMAINS.stage;
    if (host.includes("dev") || host.includes("localhost") || host === "127.0.0.1") {
      return LIVE_DOMAINS.dev;
    }
    // Any other host (e.g. trucktrack.zone, app.trucktrack.zone) is treated as production
    return LIVE_DOMAINS.prod;
  }

  return LIVE_DOMAINS.dev;
}

/**
 * Generates full client live tracking URL for a Job
 * Format: {live url}/job/{link}
 */
export function getClientJobLiveUrl(jobLink) {
  if (!jobLink) return "";
  const base = getLiveBaseUrl();
  return `${base}/job/${jobLink}`;
}

/**
 * Generates full live tracking URL for a Yard
 * Format: {live url}/yard/{link}
 */
export function getYardLiveUrl(yardLink) {
  if (!yardLink) return "";
  const base = getLiveBaseUrl();
  return `${base}/yard/${yardLink}`;
}

/**
 * Copies text to clipboard and returns a promise
 */
export async function copyToClipboard(text) {
  if (!text) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand("copy");
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    console.warn("Failed to copy live tracking link to clipboard:", err);
    return false;
  }
}
